
import React, { useState } from 'react';
import { useLanguage } from './LanguageContext';
import { CONFIG, TRANSLATIONS } from '../constants';
import { Send, CheckCircle, MapPin, Phone, Mail } from 'lucide-react';

const ContactSection: React.FC = () => {
  const { language } = useLanguage();
  const t = TRANSLATIONS[language];
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    honeypot: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok) {
        setIsSuccess(true);
        setFormData({ name: '', email: '', phone: '', subject: '', message: '', honeypot: '' });
        setTimeout(() => setIsSuccess(false), 5000);
      } else {
        setError(result.error || 'Failed to send message');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-[#0a0a0a] text-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-16">
          <span className="text-[#c5a059] font-bold text-xs tracking-[0.4em] uppercase mb-4 block">Confidential Consultation</span>
          <h2 className="text-4xl md:text-6xl font-serif font-bold mb-6">{t.contactTitle}</h2>
          <div className="w-24 h-1.5 bg-[#c5a059] mx-auto mb-8"></div>
          <p className="text-gray-400 mb-10 text-xl max-w-3xl mx-auto font-light leading-relaxed">{t.contactSubtitle}</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-stretch">
          {/* Form Side */}
          <div className="bg-white/5 p-8 md:p-12 border border-white/10 rounded-sm">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Honeypot Trap */}
              <div className="hidden">
                <input
                  type="text"
                  name="honeypot"
                  tabIndex={-1}
                  autoComplete="off"
                  value={formData.honeypot}
                  onChange={handleChange}
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-8">
                <div className="flex flex-col">
                  <label className="block text-xs font-bold uppercase tracking-[0.2em] text-[#c5a059] mb-3">{t.formName}</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full bg-white/5 border-b border-white/20 p-4 focus:outline-none focus:border-[#c5a059] transition-all duration-300 focus:bg-white/10"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="block text-xs font-bold uppercase tracking-[0.2em] text-[#c5a059] mb-3">{t.formEmail}</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full bg-white/5 border-b border-white/20 p-4 focus:outline-none focus:border-[#c5a059] transition-all duration-300 focus:bg-white/10"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-8">
                <div className="flex flex-col">
                  <label className="block text-xs font-bold uppercase tracking-[0.2em] text-[#c5a059] mb-3">{t.formPhone}</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-white/5 border-b border-white/20 p-4 focus:outline-none focus:border-[#c5a059] transition-all duration-300 focus:bg-white/10"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="block text-xs font-bold uppercase tracking-[0.2em] text-[#c5a059] mb-3">Subject</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="Case Subject"
                    className="w-full bg-white/5 border-b border-white/20 p-4 focus:outline-none focus:border-[#c5a059] transition-all duration-300 focus:bg-white/10 placeholder:text-gray-600"
                  />
                </div>
              </div>

              <div className="flex flex-col">
                <label className="block text-xs font-bold uppercase tracking-[0.2em] text-[#c5a059] mb-3">{t.formMessage}</label>
                <textarea
                  rows={4}
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="How can we assist you today?"
                  className="w-full bg-white/5 border-b border-white/20 p-4 focus:outline-none focus:border-[#c5a059] transition-all duration-300 focus:bg-white/10 resize-none placeholder:text-gray-600"
                ></textarea>
              </div>

              {error && (
                <div className="text-red-500 text-sm bg-red-500/10 p-4 rounded-sm border border-red-500/20">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-5 font-bold tracking-[0.3em] transition-all duration-500 flex items-center justify-center gap-3 shadow-2xl ${isSuccess ? 'bg-green-600' : 'bg-[#c5a059] hover:bg-[#b08b49] hover:-translate-y-1'}`}
              >
                {isSubmitting ? (
                  <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : isSuccess ? (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    MESSAGE SENT
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    {t.formSubmit}
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Info & Map Side */}
          <div className="flex flex-col gap-8">
            <div className="grid gap-4">
              <div className="bg-white/5 border border-white/10 p-6 flex items-start gap-5 group hover:bg-white/10 transition-colors">
                <div className="bg-[#c5a059]/20 p-3 rounded-full">
                  <MapPin className="text-[#c5a059] w-6 h-6 shrink-0" />
                </div>
                <div>
                  <h4 className="font-bold text-xs tracking-widest uppercase mb-1 text-[#c5a059]">Office Location</h4>
                  <p className="text-gray-300 text-sm font-light">{CONFIG.address}</p>
                </div>
              </div>
              <div className="bg-white/5 border border-white/10 p-6 flex items-start gap-5 group hover:bg-white/10 transition-colors">
                <div className="bg-[#c5a059]/20 p-3 rounded-full">
                  <Phone className="text-[#c5a059] w-6 h-6 shrink-0" />
                </div>
                <div>
                  <h4 className="font-bold text-xs tracking-widest uppercase mb-1 text-[#c5a059]">Direct Line</h4>
                  <p className="text-gray-300 text-sm font-light">{CONFIG.phoneNumber}</p>
                </div>
              </div>
              <div className="bg-white/5 border border-white/10 p-6 flex items-start gap-5 group hover:bg-white/10 transition-colors">
                <div className="bg-[#c5a059]/20 p-3 rounded-full">
                  <Mail className="text-[#c5a059] w-6 h-6 shrink-0" />
                </div>
                <div>
                  <h4 className="font-bold text-xs tracking-widest uppercase mb-1 text-[#c5a059]">Email Support</h4>
                  <p className="text-gray-300 text-sm font-light">{CONFIG.email}</p>
                </div>
              </div>
            </div>

            <div className="flex-grow min-h-[250px] relative rounded-sm overflow-hidden border border-white/10 shadow-2xl">
              <iframe
                src={CONFIG.mapsUrl}
                className="absolute inset-0 w-full h-full grayscale invert opacity-70"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
